import { useCallback, useEffect, useState } from "react";
import { apiListApprovalRecords } from "../api/client";

/**
 * 共享的「待我审批」计数。
 *
 * 为什么做成模块级单例（而不是各处各自轮询）：顶栏「协同办公」页签角标常驻在壳层里，
 * 其它视图（如审批工作台）也可能要这个数字，各自 `usePolling` 会让同一个接口被重复请求。
 * 这里第一个订阅者启动轮询、最后一个退订时停止，所有订阅者共享同一份值。
 * （驾驶舱 KPI 卡曾是第二个订阅者，2026-09-24 该卡按需求移除，现仅顶栏角标消费。）
 *
 * 口径：网关没有专用计数接口，只能用 `GET /approval/records?status=PENDING&mine=true`
 * 的**列表条数**（与驾驶舱此前的取数方式一致）。仅在 `enabled`（角色有 /approval 访问权）时请求。
 *
 * 失败时**保留上一次的值**：把角标清成 0 会让「请求失败」看起来像「没有待办」。
 */

const POLL_MS = 60_000;
/** 路由变化会触发 refresh，这里设一个最小间隔，避免连续跳页时把接口打爆 */
const MIN_REFRESH_GAP_MS = 2_000;

let cachedCount = 0;
let lastFetchedAt = 0;
let inFlight = false;
let timer: ReturnType<typeof setInterval> | null = null;
const subscribers = new Set<(value: number) => void>();

function publish(value: number) {
  cachedCount = value;
  subscribers.forEach((notify) => notify(value));
}

function fetchPendingApprovals() {
  if (inFlight) return;
  if (Date.now() - lastFetchedAt < MIN_REFRESH_GAP_MS) return;
  inFlight = true;
  void (async () => {
    try {
      const resp = await apiListApprovalRecords({ status: "PENDING", mine: "true" });
      const body = (resp.data as Record<string, unknown> | undefined) ?? {};
      const records = ((body.data as Record<string, unknown> | undefined)?.records ?? body.records) as unknown;
      if (Array.isArray(records)) publish(records.length);
      lastFetchedAt = Date.now();
    } catch (err) {
      // 保留旧值，仅留痕 —— 角标不清零
      console.warn("[approval-count] 拉取待审批数量失败，沿用上一次的值", err);
    } finally {
      inFlight = false;
    }
  })();
}

function ensureTimer() {
  if (timer) return;
  timer = setInterval(fetchPendingApprovals, POLL_MS);
}

function stopTimerIfIdle() {
  if (subscribers.size === 0 && timer) {
    clearInterval(timer);
    timer = null;
  }
}

export interface PendingApprovalCount {
  /** 待我审批的单据数；未启用（无权限）时为 0 */
  count: number;
  /** 立即刷新（内部有 2s 最小间隔；并发调用会自动合并） */
  refresh: () => void;
}

export function usePendingApprovalCount(enabled: boolean): PendingApprovalCount {
  const [value, setValue] = useState(cachedCount);

  /**
   * ⚠️ 这里必须看订阅者集合：`enabled=false` 的角色（如项目经理、操作员）不该打这个接口 ——
   * 网关对 `/approval/records` 校验 `approval:read`，无权限就是 **403**。
   * 而壳层在每次路由变化时都会调用 `refresh()`，此前它无条件发请求，导致**每页一条 403 控制台噪声**
   *（2026-09-25 定位：`canAccessRoute` 闸门是对的，漏的是 refresh 这条旁路）。
   */
  const refresh = useCallback(() => {
    if (subscribers.size === 0) return;
    fetchPendingApprovals();
  }, []);

  useEffect(() => {
    if (!enabled) return;
    subscribers.add(setValue);
    // 已有缓存值先同步给新订阅者，避免角标先显示 0 再跳数
    setValue(cachedCount);
    fetchPendingApprovals();
    ensureTimer();
    return () => {
      subscribers.delete(setValue);
      stopTimerIfIdle();
    };
  }, [enabled]);

  return { count: enabled ? value : 0, refresh };
}

/** 仅供单测重置模块级状态用（避免用例之间互相污染） */
export function __resetPendingApprovalCountForTest() {
  cachedCount = 0;
  lastFetchedAt = 0;
  inFlight = false;
  subscribers.clear();
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}
